import { Link } from "react-router-dom";
import styles from "./CustomModal.module.css";

const CustomModal = ({
  state,
  message,
  title,
  description,
  primaryLabel,
  secondaryLabel,
  onPrimaryAction,
  onClose,
}) => {
  const config = STATE_DEFAULTS[state];
  if (!config) return null;

  const resolvedTitle = title ?? config.title;
  const resolvedDescription = description ?? config.description;
  const resolvedMessage = message ?? config.message;
  const resolvedPrimaryLabel = primaryLabel ?? config.primaryLabel;
  const resolvedSecondaryLabel = secondaryLabel ?? config.secondaryLabel;
  const role = localStorage.getItem("role");

  const titleId = `custom-modal-title-${state}`;

  return (
    <div
      className={styles.overlay}
      role={config.role}
      aria-modal="true"
      aria-live={config.ariaLive}
      aria-labelledby={titleId}
    >
      <div className={styles.modal}>
        <config.Icon />

        {state === "locked" ? (
          <h3 id={titleId} className={styles.titleInline}>
            {resolvedMessage}
          </h3>
        ) : (
          <>
            <p id={titleId} className={styles.title}>
              {resolvedTitle}
            </p>
            <p className={styles.desc}>{resolvedDescription}</p>
          </>
        )}

        <div className={styles.actions}>
          <button
            type="button"
            className={styles.cta}
            onClick={onPrimaryAction}
          >
            {resolvedPrimaryLabel}
          </button>
          {state === "failed" && role && (
            <Link
              className={styles.link}
              to={
                role?.toLowerCase() === "talent"
                  ? "/talent-report"
                  : "/client-report"
              }
              onClick={onClose}
            >
              {resolvedSecondaryLabel}
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

/* ── Icons — ported verbatim (same paths/geometry) from the design doc's inline SVGs ── */

function LockedIcon() {
  return (
    <svg
      className={styles.icon}
      viewBox="0 0 200 160"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <circle cx="100" cy="80" r="70" fill="rgba(255,255,255,0.06)" />
      <path
        d="M78,74 a22,22 0 0 1 44,0"
        stroke="#3b5fe8"
        strokeWidth="10"
        fill="none"
        strokeLinecap="round"
      />
      <rect x="68" y="70" width="64" height="50" rx="12" fill="#d97706" />
      <rect
        x="68"
        y="70"
        width="64"
        height="14"
        rx="12"
        fill="#ffffff"
        opacity="0.14"
      />
      <circle cx="100" cy="90" r="7" fill="#0d1b3e" />
      <rect x="97" y="94" width="6" height="14" rx="2" fill="#0d1b3e" />
    </svg>
  );
}

function SuccessIcon() {
  return (
    <svg
      className={styles.icon}
      viewBox="0 0 200 160"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <circle cx="100" cy="80" r="70" fill="rgba(255,255,255,0.06)" />
      <circle cx="100" cy="80" r="34" fill="#059669" />
      <path
        d="M84,81 L95,92 L118,66"
        stroke="#ffffff"
        strokeWidth="7"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function FailedIcon() {
  return (
    <svg
      className={styles.icon}
      viewBox="0 0 200 160"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <circle cx="100" cy="80" r="70" fill="rgba(255,255,255,0.06)" />
      <circle cx="100" cy="80" r="34" fill="#dc2626" />
      <path
        d="M87,67 L113,93"
        stroke="#ffffff"
        strokeWidth="7"
        fill="none"
        strokeLinecap="round"
      />
      <path
        d="M113,67 L87,93"
        stroke="#ffffff"
        strokeWidth="7"
        fill="none"
        strokeLinecap="round"
      />
    </svg>
  );
}

const STATE_DEFAULTS = {
  locked: {
    Icon: LockedIcon,
    role: "dialog",
    ariaLive: undefined,
    message:
      "Sorry you have not completed your onboarding. In order to proceed, please complete your process.",
    primaryLabel: "Complete Onboarding",
  },
  success: {
    Icon: SuccessIcon,
    role: "dialog",
    ariaLive: "polite",
    title: "Onboarding complete",
    description:
      "Your profile is ready. You now have full access to Advanced Search, Job Feed, and AI Insights.",
    primaryLabel: "Start Exploring Matches",
  },
  failed: {
    Icon: FailedIcon,
    role: "alertdialog",
    ariaLive: "assertive",
    title: "We couldn't complete your onboarding",
    description:
      "Your answers have been saved as a draft — check your connection and try again. If this keeps happening, our support team can help.",
    primaryLabel: "Try Again",
    secondaryLabel: "Contact Support",
  },
};

export default CustomModal;
